import { Component, EventEmitter, Input, OnInit, Output, signal } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef, NgbPopover, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { AlertService } from '../../../alert.service';
import { faTrashCan, faPlus, faEdit, faRotateLeft, faToggleOn, faToggleOff, faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { NgIf, NgFor, CommonModule } from '@angular/common';
import { Invoice } from '@main/invoice';
import { CreditNote } from '@app-shared/interfaces/credit-notes/credit-notes.model';
import { CreditNotesService } from '@app-shared/services/credit-notes.service';
import { CreditNoteReasonPipe } from '@app-shared/pipes/credit-note-reason.pipe';

@Component({
  selector: 'app-credit-note-list',
  imports: [NgIf, FaIconComponent, NgFor, NgbTooltip, ReactiveFormsModule, CommonModule, NgbPopover, CreditNoteReasonPipe],
  templateUrl: './credit-note-list.component.html',
  styleUrl: './credit-note-list.component.css'
})
export class CreditNoteListComponent implements OnInit {

  // Inputs and Outputs
  @Input() cardBodyHeight: string = "100%";
  @Input() invoiceId: string | null = null;
  @Input() clientId: string | null = null;
  @Input() type: string | null = null;

  @Input() set invoice(value: any) {
    this._invoice = value;
    if (value?.clientId) {
      this.clientId = value.clientId;
      this.getCreditNotes();
      this.filterReasonOptions();
    }
  }

  get invoice(): Invoice {
    return this._invoice;
  }

  @Input() set refreshCreditNotes(value: boolean) {
    this._refreshCreditNotes = value;
    if (value) {
      this.getCreditNotes();
    }
  }

  get refreshCreditNotes(): boolean {
    return this._refreshCreditNotes;
  }

  @Output() applyCreditNoteSuccess: EventEmitter<boolean> = new EventEmitter<boolean>(false);

  // Modals
  private modalReference: NgbModalRef;

  // Forms
  public creditNoteForm: UntypedFormGroup;
  public applyCreditNoteForm: UntypedFormGroup;

  // Properties
  public creditNoteList = signal<CreditNote[]>([]);
  public selectedCreditNote: CreditNote;
  public reasonOptions: { label: string; value: string }[] = [];
  public defaultReasonOptions = [
    { label: 'Overpayment', value: 'OVERPAYMENT' },
    { label: 'Referral', value: 'REFERRAL' },
    { label: 'Discount', value: 'DISCOUNT' },
    { label: 'Refund', value: 'REFUND' },
    { label: 'Other', value: 'OTHER' }
  ];
  public showOtherReasonInput: boolean = false;
  public applyCreditNoteDisabled: boolean = false;
  public createCreditNoteDisabled: boolean = false;
  public showZeroBalances: boolean = false;
  private _refreshCreditNotes: boolean = false;
  private _invoice: Invoice | null = null;


  // Font Awesome Properties
  faTrashCan = faTrashCan;
  faPlus = faPlus;
  faEdit = faEdit;
  faRotateLeft = faRotateLeft;
  faToggleOn = faToggleOn;
  faToggleOff = faToggleOff;
  faQuestionCircle = faQuestionCircle;

  constructor(private modalService: NgbModal,
    private alertService: AlertService,
    private creditNoteService: CreditNotesService) { }

  ngOnInit(): void {
    this.creditNoteForm = new UntypedFormGroup({
      amount: new UntypedFormControl([Validators.required]),
      reason: new UntypedFormControl([Validators.required]),
      notes: new UntypedFormControl(),
      cashExchanged: new UntypedFormControl()
    });

    this.applyCreditNoteForm = new UntypedFormGroup({
      amount: new UntypedFormControl([Validators.required]),
    });
    this.filterReasonOptions();
    this.getCreditNotes();
  }

  ngOnDestroy(): void {
    this.modalService.hasOpenModals() && this.modalService.dismissAll();
  }

  // Filter Reason Options
  filterReasonOptions() {
    if (this.type === 'CLIENT' || this.invoice?.amountDue >= 0) {
      this.reasonOptions = this.defaultReasonOptions.filter((option) => option.value !== 'OVERPAYMENT');
      this.creditNoteForm.controls['reason'].patchValue('REFERRAL');
    } else {
      this.reasonOptions = this.defaultReasonOptions;
      this.creditNoteForm.controls['reason'].patchValue('OVERPAYMENT');
    }
  }

  // Get Credit Notes
  private getCreditNotes(): void {
    if (this.clientId) {
      this.creditNoteService.getCreditNotesByClientId(this.clientId, this.showZeroBalances).subscribe((res: CreditNote[]) => {
        this.creditNoteList.set(res);
      });
    }
  }

  // Parse Credit Note Amount
  public parseCreditNoteAmount(amount: number) {
    return (+amount).toFixed(2);
  }

  // Delete Credit Note
  deleteCreditNote(creditNote: CreditNote) {
    this.creditNoteService.deleteCreditNote(creditNote.id).subscribe((res) => {
      this.alertService.showSuccessAlert('Credit Note Deleted');
      this.getCreditNotes();
    });
  }

  // Relinquish Credit Note
  relinquishCreditNote(note: any) {
    // TODO: Call API to relinquish credit
  }

  // Open Create Credit Note Modal
  openCreateCreditNoteModal(content: any): void {
    this.selectedCreditNote = null;
    this.creditNoteForm.reset();
    this.creditNoteForm.controls['cashExchanged'].patchValue(false);
    this.filterReasonOptions();
    this.modalReference = this.modalService.open(content);
  }

  // Create Credit Note Form
  createCreditNoteForm() {
    if (this.type === 'INVOICE' && (!this.invoice || !this.invoice?.id)) {
      this.alertService.showErrorAlert('Invoice ID is missing.');
      return;
    }
    if (this.creditNoteForm.invalid) return;
    this.createCreditNoteDisabled = true;
    let payload = {
      clientId: this.clientId,
      amount: +this.creditNoteForm.value.amount.toFixed(2),
      reason: this.creditNoteForm.value.reason,
      notes: this.creditNoteForm.value.notes,
      isCashExchanged: this.creditNoteForm.value.cashExchanged || false
    }
    if (this.creditNoteForm.value.reason === 'OVERPAYMENT') {
      payload['invoiceAssociatedId'] = this.invoice?.id;
    }
    this.creditNoteService.createCreditNote(payload).subscribe({
      next: (res) => {
        if (res) {
          this.alertService.showSuccessAlert('Credit Note Added');
          this.getCreditNotes();
          this.createCreditNoteDisabled = false;
          this.creditNoteForm.reset();
          this.modalReference.close();
        }
      },
      error: () => {
        this.createCreditNoteDisabled = false;
      }
    });
  }

  // Open Apply Modal
  openApplyModal(content: any, creditNote: any, event: Event) {
    if (creditNote.remainingBalance <= 0) {
      return;
    }
    event.stopPropagation();
    this.applyCreditNoteForm.reset();
    this.selectedCreditNote = creditNote;
    this.applyCreditNoteForm.controls['amount'].setValue(creditNote.amount);
    this.modalReference = this.modalService.open(content);
  }

  // Apply Credit Note
  applyCreditNote(event: Event) {
    event.stopPropagation();
    if (!this.invoice?.id) {
      this.alertService.showErrorAlert('Invoice id missing');
      this.applyCreditNoteDisabled = false;
      return;
    }
    if (this.applyCreditNoteAmount.value <= 0 || this.applyCreditNoteAmount.value > this.selectedCreditNoteRemainingBalance) {
      if (this.applyCreditNoteAmount.value === 0) {
        this.alertService.showErrorAlert('Amount should be greater than zero.');
        return;
      }
      return this.alertService.showErrorAlert('Amount should be greater than zero and less than or equal to credit note amount.');
    }
    this.applyCreditNoteDisabled = true;
    const payload = {
      invoiceId: this.invoice.id,
      amount: +this.applyCreditNoteForm.value.amount,
      date: new Date().toISOString().split('T')[0]
    }
    this.creditNoteService.applyCreditNote(this.selectedCreditNote?.id, payload).subscribe({
      next: (res) => {
        if (res) {
          this.alertService.showSuccessAlert('Credit Note Applied');
          this.getCreditNotes();
          this.applyCreditNoteSuccess.emit(true);
          this.applyCreditNoteForm.reset();
          this.modalReference.close();
          this.applyCreditNoteDisabled = false;
        }
      },
      error: (err) => {
        this.applyCreditNoteDisabled = false;
      }
    });
  }

  // Toggle Show Zero Balances
  toggleShowZeroBalances() {
    this.showZeroBalances = !this.showZeroBalances;
    this.getCreditNotes();
  }

  // Check Invoice Id In Applied Invoices
  checkInvoiceIdInAppliedInvoices(appliedInvoices, invoiceId) {
    return appliedInvoices.includes(invoiceId);
  }

  get selectedCreditNoteRemainingBalance() {
    return Number(this.selectedCreditNote?.remainingBalance) || 0;
  }

  public get amount() { return this.creditNoteForm.get('amount'); }
  public get reason() { return this.creditNoteForm.get('reason'); }
  public get notes() { return this.creditNoteForm.get('notes'); }
  public get cashExchanged() { return this.creditNoteForm.get('cashExchanged'); }

  public get applyCreditNoteAmount() { return this.applyCreditNoteForm.get('amount'); }
}
